import React, { useRef, useState, useEffect, useMemo } from "react";
import PropTypes from "prop-types";
import { connect } from "react-redux";
import { userHasRight } from "selectors/rights";
import { QA_CASE_AUDIT_EDIT, QA_CASE_AUDIT_FEEDBACK_EDIT, CASE_AUDITS_OVERTURN } from "typeConstants/rights";
import { MCard, MuiTextField, MCheckboxGroup, MSpinner, MDialog, ActionButtons } from "components/Shared";
import { Button, Grid, Cell } from "react-md";
import { getSelectionTypes, retrieveAudit, createAudit, createAuditFeedback, overturnAudit } from "requests/qaCaseAudit/index.ts";
import { dateTimeToString } from "components/Shared/Formatters";
import { DATA_TYPE_NUMBER_TENTHS } from "typeConstants/dataTypes";
import Radio from '@material-ui/core/Radio';
import RadioGroup from '@material-ui/core/RadioGroup';
import FormControlLabel from '@material-ui/core/FormControlLabel';
import FormControl from '@material-ui/core/FormControl';
import responseHandler from "responseHandler";
import { PrimaryStatus } from "typeConstants/caseStatusCodes";

const QACaseAudit = ({
  controlNumber,
  caseHeader,
  hasEditPermission,
  hasFeedbackEditPermission,
  hasOverturnPermission,
  makeToast,
}) => {
  const mounted = useRef(true);
  const findingsControlMethods = useRef({});
  const commentsRef = useRef("");
  const trainingControlMethods = useRef({});
  const feedbackCommentsRef = useRef("");
  const overturnCommentsRef = useRef("");

  const [isFetchingSelectionTypes, setIsFetchingSelectionTypes] = useState(false);
  const [selectionTypes, setSelectionTypes] = useState(null);
  const [isFetchingAudit, setIsFetchingAudit] = useState(false);
  const [audit, setAudit] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [passFailValue, setPassFailValue] = useState("");
  const [auditeeAgreesValue, setAuditeeAgreesValue] = useState("");
  const [isSubmittingFeedback, setIsSubmittingFeedback] = useState(false);
  const [showAuditSelectionTypes, setShowAuditSelectionTypes] = useState(true);
  const [displayOverturningDialog, setDisplayOverturningDialog] = useState(false);  

  const isCompleted = caseHeader?.caseInfo?.primaryStatus === PrimaryStatus.COMPLETED;

  useEffect(() => {
    if (!selectionTypes) {
      setIsFetchingSelectionTypes(true);
      getSelectionTypes((data) => {
        setSelectionTypes(data);
        setIsFetchingSelectionTypes(false);
      }, mounted);
    }

    if (!audit) {
      setIsFetchingAudit(true);
      retrieveAudit(controlNumber, (data) => {
        setAudit(data);
        setIsFetchingAudit(false);
      }, mounted);
    }

    return () => { mounted.current = false; };
  }, []);

  useEffect(() => {
    if(audit && !audit.auditCompleted)
    {
      if(selectionTypes.some(s => s.code === "REJECTION"))
      {
        let availSelectionTypes = selectionTypes.filter(s => s.code !== "REJECTION");
        setSelectionTypes(availSelectionTypes);
      }
    }
  }, [audit, selectionTypes]);

  const memoizedFindingsFields = useMemo(
    () => {
      if (!audit || !selectionTypes) return [];

      return [{
        key: "audit-findings",
        control: (
          <MCheckboxGroup
            controlMethods={cm => findingsControlMethods.current = cm}
            fields={selectionTypes.map(({ code, displayName }) => ({
              value: code,
              label: displayName,
              defaultChecked: audit.auditCompleted && audit.selectedCodes.some((v) => v === code),
              disabled: audit.auditCompleted || !hasEditPermission || isSubmitting,
            }))}
            cellSize={4}
          />
        )
      }];
    },
    [audit, selectionTypes]
  );

  const getResultFields = () => {
    if (!audit || !selectionTypes) return [];

    if (audit.auditCompleted && !passFailValue)
      setPassFailValue(audit.isPassing ? "pass" : "fail");

    const onChangeHandler = (e) => setPassFailValue(e.target.value);

    let fields = [];
    if(audit.auditCompleted){
      fields.push(
        {
          key: "audit-passfail",
          control: (
            <FormControl component="fieldset" style={{ marginLeft: "15px" }}>
              <RadioGroup aria-label="passfail" name="passfail" value={passFailValue} onChange={onChangeHandler}>
                <FormControlLabel value="pass" control={<Radio />} label="Pass" disabled={audit.auditCompleted || isSubmitting} />
                <FormControlLabel value="fail" control={<Radio />} label="Fail" disabled={audit.auditCompleted || isSubmitting} />
              </RadioGroup>
            </FormControl>
          )
        }        
      );
    }
    fields.push(
      {
        key: "audit-comments",
        control: (
          <MuiTextField
            placeholder={"Enter a comment..."}
            inputRef={commentsRef}
            // this is so weird. you get a console warning if you set this to null (default webservice response for unaudited case),
            // but setting it to an empty string causes the field to not accept any keyboard input. using the 'defaultValue'
            // prop causes the control to not be multiline or fillwidth and still doesn't allow keyboard input.
            value={audit.comments || undefined}
            multiline
            fullWidth
            disabled={audit.auditCompleted || !hasEditPermission || isSubmitting}
          />
        )
      }       
    );    
    return fields;
  };

  const getAgentDetailsFields = () => {
    if (!audit || !selectionTypes) return [];

    return [
      { label: "QA Agent", value: audit.qaAgentName },
      { label: "QA Manager", value: audit.qaAgentManager },
      { label: "QA Director", value: audit.qaAgentDirector },
      { label: "Expected Minutes", value: audit.expectedMinutes, dataType: DATA_TYPE_NUMBER_TENTHS },
    ];
  };

  const getFeedbackFields = () => {
    if (!audit) return [];

    if (audit.auditFeedbackCompleted && !auditeeAgreesValue)
      setAuditeeAgreesValue(audit.qaAgentAgrees ? "true" : "false");

    const onChangeHandler = (e) => setAuditeeAgreesValue(e.target.value);
    return [
      {
        key: "auditee-agrees",
        control: (
          <FormControl component="fieldset" style={{ marginLeft: "15px" }}>
            <RadioGroup aria-label="auditeeAgrees" name="auditeeAgrees" value={auditeeAgreesValue} onChange={onChangeHandler}>
              <FormControlLabel value="true" control={<Radio />} label="I agree with the findings"
                disabled={!audit.auditCompleted || audit.auditFeedbackCompleted || !hasFeedbackEditPermission || isSubmittingFeedback} />
              <FormControlLabel value="false" control={<Radio />} label="I disagree with the findings"
                disabled={!audit.auditCompleted || audit.auditFeedbackCompleted || !hasFeedbackEditPermission || isSubmittingFeedback} />
            </RadioGroup>
          </FormControl>
        )
      },
      {
        key: "auditee-requests-training",
        control: (
          <MCheckboxGroup
            controlMethods={cm => trainingControlMethods.current = cm}
            fields={[{
              value: true,
              label: "Training Requested (Optional)",
              defaultChecked: audit.auditFeedbackCompleted && audit.qaAgentTrainingRequested,
              disabled: !audit.auditCompleted || audit.auditFeedbackCompleted || !hasFeedbackEditPermission || isSubmittingFeedback,
            }]}
            cellSize={4}
          />
        )
      },
      {
        key: "auditee-comments",
        control: (
          <MuiTextField
            placeholder={"Enter a comment..."}
            inputRef={feedbackCommentsRef}
            value={audit.qaAgentFeedbackComments || undefined}
            multiline
            fullWidth
            disabled={!audit.auditCompleted || audit.auditFeedbackCompleted || !hasFeedbackEditPermission || isSubmittingFeedback}
          />
        )
      }
    ];
  };

  const submitAudit = () => {
    const selectedCodes = findingsControlMethods.current.getChecked();
    const isPassing = !selectedCodes.length;

    setIsSubmitting(true);
    const comments = commentsRef.current.value;
    const localOffsetMinutes = new Date().getTimezoneOffset();

    createAudit(controlNumber, { comments, selectedCodes, isPassing, localOffsetMinutes }, () => {
      setIsSubmitting(false);
      setIsFetchingAudit(true);
      retrieveAudit(controlNumber, (data) => {
        setAudit(data);
        setIsFetchingAudit(false);
      }, mounted);
      setShowAuditSelectionTypes(false);
    }, mounted);
  };

  const submitAuditFeedback = () => {
    if (auditeeAgreesValue === null) {
      makeToast("Must select Agree or Disagree.", "error");
      return;
    }

    setIsSubmittingFeedback(true);
    const trainingRequested = trainingControlMethods.current.getChecked().length > 0;
    const comments = feedbackCommentsRef.current.value;
    const auditeeAgrees = auditeeAgreesValue === "true";

    createAuditFeedback(controlNumber, {
      AgreesWithAudit: auditeeAgrees,
      Comments: comments,
      TrainingRequested: trainingRequested
    }, () => {
      setIsSubmittingFeedback(false);
      setIsFetchingAudit(true);
      retrieveAudit(controlNumber, (data) => {
        setAudit(data);
        setIsFetchingAudit(false);
      }, mounted);
    }, mounted);
  };

  const overturn = () => {
    setIsSubmitting(true);
    const comments = overturnCommentsRef.current.value;
    const localOffsetMinutes = new Date().getTimezoneOffset();    

    if(!comments || comments.trim() === ""){
      makeToast("Comments are required to overturn an audit.", "error");
      setIsSubmitting(false);
      setDisplayOverturningDialog(false);
      return;
    }

    overturnAudit(controlNumber, { comments, userInternetUserId: 0, localOffsetMinutes }, () => {
      setIsSubmitting(false);
      setDisplayOverturningDialog(false);
      setIsFetchingAudit(true);
      retrieveAudit(controlNumber, (data) => {
        setAudit(data);
        setPassFailValue(data.isPassing ? "pass" : "fail");
        setAuditeeAgreesValue("");                      
        setIsFetchingAudit(false);
      }, mounted);
    }, () => {            
      setIsSubmitting(false);
      setDisplayOverturningDialog(false);
      makeToast("Error overturning audit.", "error");      
    }, mounted);
  };

  return (
    <>
      <Grid>
        {audit && audit.auditCompleted &&
          <Cell size={8}><div style={{ marginLeft: "16px" }}>{`Reviewed by ${audit.auditorName} on ${dateTimeToString(audit.auditDate)}`}</div></Cell>}
        {audit && audit.auditFeedbackCompleted &&
          <Cell size={8}><div style={{ marginLeft: "16px" }}>{`Feedback by ${audit.qaAgentName} on ${dateTimeToString(audit.auditFeedbackDate)}`}</div></Cell>}
        {audit && audit.auditOverturnedDate &&
          <Cell size={8}><div style={{ marginLeft: "16px" }}>{`Overturned by ${audit.auditOverturnedByName} on ${dateTimeToString(audit.auditOverturnedDate)}`}</div></Cell>}          
        {hasOverturnPermission && audit && audit.auditDate && !audit.auditOverturnedDate &&
          <Cell size={4} >
            <div style={{ marginLeft: "50%" }}>
              <Button
                onClick={() => setDisplayOverturningDialog(true)}
                raised
                primary
                children={"Overturn"}
                disabled={displayOverturningDialog} 
              />
            </div>
          </Cell>}
        {showAuditSelectionTypes && <>
        <Cell size={12}>
          <MCard title={"Audit Findings"}
            isFetching={isFetchingSelectionTypes || isFetchingAudit}
            fields={memoizedFindingsFields}
          />
        </Cell>
        <Cell size={12}>
          <MCard title={"Audit Result"}
            isFetching={isFetchingSelectionTypes || isFetchingAudit}
            fields={getResultFields()}
          />
        </Cell></>}
        { isCompleted && <>
        <Cell size={12}>
          <MCard title={"Agent Detail"}
            isFetching={isFetchingSelectionTypes || isFetchingAudit}
            fields={getAgentDetailsFields()}
          />
        </Cell>
        <Cell size={12}>
          <MCard title={"Audit Feedback"}
            isFetching={isFetchingAudit}
            fields={getFeedbackFields()}
          />
        </Cell>
        </>}
        {hasEditPermission &&
          <Cell size={12}>
            <Button
              onClick={submitAudit}
              raised
              primary
              children={"Submit Audit"}
              disabled={!audit || audit.auditCompleted || isSubmitting}
            />
            {isSubmitting && <MSpinner />}
          </Cell>}
        {hasFeedbackEditPermission && isCompleted &&
          <Cell size={12}>
            <Button
              onClick={submitAuditFeedback}
              raised
              primary
              children={"Submit Audit Feedback"}
              disabled={!audit || !audit.auditCompleted || audit.auditFeedbackCompleted || isSubmitting}
            />
            {isSubmitting && <MSpinner />}
            {isSubmittingFeedback && <MSpinner />}
          </Cell>}
      </Grid>
      <MDialog id="overturn-audit-dialog" visible={displayOverturningDialog} title="Overturn Audit" width={400}>
        Enter comments describing why this audit is being overturned:
        <MuiTextField
          placeholder={"Enter a comment..."}
          inputRef={overturnCommentsRef}
          multiline
          fullWidth
          disabled={(audit?.auditOverturnedDate) || !hasOverturnPermission}
        />
        <ActionButtons buttonPropSets={[
          { children: "Submit", onClick: () => overturn() },
          { children: "Cancel", onClick: () => setDisplayOverturningDialog(false) },
        ]} />        
      </MDialog>
    </>
  );
};

QACaseAudit.propTypes = {
  controlNumber: PropTypes.number.isRequired,
  caseHeader: PropTypes.object,

  // redux state
  hasEditPermission: PropTypes.bool.isRequired,
  hasFeedbackEditPermission: PropTypes.bool.isRequired,
  hasOverturnPermission: PropTypes.bool.isRequired,
  // redux dispatch
  makeToast: PropTypes.func,
};

const mapStateToProps = state => {
  return {
    caseHeader: state.caseForms.caseHeader,
    hasEditPermission: userHasRight(QA_CASE_AUDIT_EDIT)(state),
    hasFeedbackEditPermission: userHasRight(QA_CASE_AUDIT_FEEDBACK_EDIT)(state),
    hasOverturnPermission: userHasRight(CASE_AUDITS_OVERTURN)(state),
  };
};

const mapDispatchToProps = dispatch => ({
  makeToast(text, type) {
    responseHandler.customToast(text, type, dispatch);
  },
});

export default connect(mapStateToProps, mapDispatchToProps)(QACaseAudit);
